/**
 * The app's main navigation: a dark sidebar that collapses to an icon rail.
 *
 * Expanded it shows the group labels and each tool's name; collapsed, only the
 * icons, each with a tooltip and an `aria-label` carrying the name. The active
 * page has one style for every tool — the per-tool accent colours it replaced
 * made the sidebar read as fifteen different products.
 */

import { LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useEffect, useState } from 'react'

import { useAuth } from '../../hooks/useAuth'
import { NAV_GROUPS, type NavItem, type View } from '../../navigation'
import { cn } from '../ui/styles'
import Tooltip from '../ui/Tooltip'
import HkcmMark from './HkcmMark'
import ThemeSwitch from './ThemeSwitch'

interface SidebarProps {
    activeView: View
    onNavigate: (view: View) => void
    collapsed: boolean
    onToggleCollapsed: () => void
}

interface VersionInfo {
    version?: string
    git_commit?: string | null
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent)
const TOGGLE_SHORTCUT = IS_MAC ? '⌘B' : 'Strg+B'

const FOCUS_RING =
    'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-400 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar'

function initials(name: string): string {
    const words = name.replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean)
    const letters = words.length > 1 ? words[0][0] + words[1][0] : (words[0] ?? '?').slice(0, 2)
    return letters.toUpperCase()
}

function useVersion(): VersionInfo | null {
    const [version, setVersion] = useState<VersionInfo | null>(null)
    useEffect(() => {
        let cancelled = false
        fetch('/api/version')
            .then((res) => (res.ok ? res.json() : null))
            .then((data: VersionInfo | null) => {
                if (!cancelled && data?.version) setVersion(data)
            })
            .catch(() => {
                // The version is decoration; a failed fetch shows nothing.
            })
        return () => {
            cancelled = true
        }
    }, [])
    return version
}

export default function Sidebar({ activeView, onNavigate, collapsed, onToggleCollapsed }: SidebarProps) {
    const { user, logout, authEnabled } = useAuth()
    const version = useVersion()
    const toggleLabel = collapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen'
    const displayName = user?.name || user?.username || 'Angemeldet'
    const email = user?.username && user.username !== displayName ? user.username : null

    return (
        <aside
            id="app-sidebar"
            data-collapsed={collapsed}
            className={cn(
                'flex h-full shrink-0 flex-col overflow-hidden bg-sidebar text-gray-300 transition-[width] duration-200 ease-out motion-reduce:transition-none',
                collapsed ? 'w-16' : 'w-64',
            )}
        >
            <div
                className={`flex h-16 shrink-0 items-center border-b border-white/10 ${
                    collapsed ? 'justify-center px-2' : 'justify-between pl-5 pr-3'
                }`}
            >
                {!collapsed && (
                    <span className="flex items-center gap-2.5 whitespace-nowrap">
                        <HkcmMark onDark className="h-7 w-auto" />
                        <span className="text-[17px] font-semibold tracking-[0.16em] text-white">HKCM</span>
                        <span className="-ml-1 text-[17px] font-medium text-blue-300">News</span>
                    </span>
                )}
                <Tooltip label={`${toggleLabel} (${TOGGLE_SHORTCUT})`} side={collapsed ? 'right' : 'bottom'}>
                    <button
                        type="button"
                        onClick={onToggleCollapsed}
                        aria-label={toggleLabel}
                        aria-expanded={!collapsed}
                        aria-controls="app-sidebar-nav"
                        aria-keyshortcuts={IS_MAC ? 'Meta+B' : 'Control+B'}
                        className={`flex h-9 w-9 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-white/10 hover:text-white ${FOCUS_RING}`}
                    >
                        {collapsed ? (
                            <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
                        ) : (
                            <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
                        )}
                    </button>
                </Tooltip>
            </div>

            <nav
                id="app-sidebar-nav"
                aria-label="Hauptnavigation"
                className="flex-1 overflow-y-auto overflow-x-hidden px-3 py-4 scrollbar-thin"
            >
                {NAV_GROUPS.map((group, index) => (
                    <div key={group.label} role="group" aria-label={group.label} className={index > 0 ? 'mt-4' : undefined}>
                        {collapsed ? (
                            index > 0 && <div aria-hidden="true" className="mx-2 mb-4 border-t border-white/10" />
                        ) : (
                            <p aria-hidden="true" className="mb-1 whitespace-nowrap px-3 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                                {group.label}
                            </p>
                        )}
                        <ul className="space-y-0.5">
                            {group.items.map((item) => (
                                <li key={item.view}>
                                    <NavButton
                                        item={item}
                                        active={item.view === activeView}
                                        collapsed={collapsed}
                                        onNavigate={onNavigate}
                                    />
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </nav>

            <div className={`shrink-0 border-t border-white/10 ${collapsed ? 'flex flex-col items-center gap-1 px-2 py-3' : 'p-3'}`}>
                <div className={collapsed ? 'mb-2 flex justify-center' : 'mb-3 px-1'}>
                    <ThemeSwitch collapsed={collapsed} />
                </div>
                <div className={`flex items-center ${collapsed ? 'flex-col gap-1' : 'gap-3 px-2'}`}>
                    <Tooltip label={email ? `${displayName} · ${email}` : displayName} enabled={collapsed}>
                        <span
                            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-600/20 text-xs font-semibold text-blue-300 ring-1 ring-inset ring-blue-400/30"
                            aria-hidden={!collapsed}
                            aria-label={collapsed ? displayName : undefined}
                            role={collapsed ? 'img' : undefined}
                        >
                            {initials(displayName)}
                        </span>
                    </Tooltip>
                    {!collapsed && (
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-white">{displayName}</p>
                            {email && <p className="truncate text-xs text-gray-400">{email}</p>}
                        </div>
                    )}
                    {authEnabled && (
                        <Tooltip label="Abmelden" enabled={collapsed}>
                            <button
                                type="button"
                                onClick={logout}
                                aria-label="Abmelden"
                                title={collapsed ? undefined : 'Abmelden'}
                                className={`flex h-8 w-8 items-center justify-center rounded-full text-gray-300 transition-colors hover:bg-red-500/15 hover:text-red-300 ${FOCUS_RING}`}
                            >
                                <LogOut className="h-4 w-4" aria-hidden="true" />
                            </button>
                        </Tooltip>
                    )}
                </div>
                {!collapsed && version?.version && (
                    <p className="mt-2 px-2 text-[11px] text-gray-400">
                        v{version.version}
                        {version.git_commit && <span className="ml-1">· {version.git_commit.slice(0, 7)}</span>}
                    </p>
                )}
            </div>
        </aside>
    )
}

function NavButton({
    item,
    active,
    collapsed,
    onNavigate,
}: {
    item: NavItem
    active: boolean
    collapsed: boolean
    onNavigate: (view: View) => void
}) {
    const Icon = item.icon
    return (
        <Tooltip label={item.label} enabled={collapsed}>
            <button
                type="button"
                onClick={() => onNavigate(item.view)}
                aria-current={active ? 'page' : undefined}
                aria-label={collapsed ? item.label : undefined}
                className={cn(
                    'flex w-full items-center rounded-full text-sm font-medium transition-colors',
                    FOCUS_RING,
                    collapsed ? 'h-10 justify-center' : 'h-9 gap-3 px-3',
                    active ? 'bg-hyperblau text-white shadow-xs' : 'text-gray-300 hover:bg-white/5 hover:text-white',
                )}
            >
                <Icon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />
                {!collapsed && <span className="truncate">{item.label}</span>}
            </button>
        </Tooltip>
    )
}
